import React, { useState, useEffect } from 'react';
import { AlertTriangle, Bell, X, ChevronRight, Briefcase, Calendar, Tag, Megaphone, Gift, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { alertsApi } from '../services/api';

const categoryIcons = {
  operacional: Bell,
  moderacao: AlertTriangle,
  comercial: Building2,
  oportunidade: Briefcase,
  institucional: Megaphone,
  evento: Calendar,
  promocao: Gift,
  vaga: Briefcase,
};

const categoryColors = {
  operacional: 'bg-blue-50 border-blue-200 text-blue-800',
  moderacao: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  comercial: 'bg-green-50 border-green-200 text-green-800',
  oportunidade: 'bg-purple-50 border-purple-200 text-purple-800',
  institucional: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  evento: 'bg-pink-50 border-pink-200 text-pink-800',
  promocao: 'bg-orange-50 border-orange-200 text-orange-800',
  vaga: 'bg-teal-50 border-teal-200 text-teal-800',
};

const priorityStyles = {
  alta: 'border-l-4 border-l-red-500',
  media: 'border-l-4 border-l-yellow-500',
  baixa: 'border-l-4 border-l-green-500',
};

// Componente para exibir alertas na Home
export function PublicAlertsHome({ municipioId }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);

  useEffect(() => {
    loadAlerts();
    // Load dismissed alerts from localStorage
    const dismissed = JSON.parse(localStorage.getItem('dismissedPublicAlerts') || '[]');
    setDismissedAlerts(dismissed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioId]);

  const loadAlerts = async () => {
    try {
      const params = { exibir_home: 'true' };
      if (municipioId) params.municipio_id = municipioId;
      
      const data = await alertsApi.listPublic(params);
      setAlerts(data);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (alertId, e) => {
    e.stopPropagation();
    const newDismissed = [...dismissedAlerts, alertId];
    setDismissedAlerts(newDismissed);
    localStorage.setItem('dismissedPublicAlerts', JSON.stringify(newDismissed));
  };

  const handleClick = async (alert) => {
    // Track click
    try {
      await alertsApi.trackPublic(alert.id, 'click');
    } catch (error) {
      console.error('Erro ao rastrear clique:', error);
    }
    
    if (alert.cta_url) {
      if (alert.cta_url.startsWith('http')) {
        window.open(alert.cta_url, '_blank');
      } else {
        navigate(alert.cta_url);
      }
    }
  };

  const visibleAlerts = alerts.filter(a => !dismissedAlerts.includes(a.id));

  if (loading || visibleAlerts.length === 0) return null;

  return (
    <div className="space-y-3 mb-6">
      {visibleAlerts.map(alert => {
        const Icon = categoryIcons[alert.categoria] || Bell;
        const colorClass = categoryColors[alert.categoria] || 'bg-gray-50 border-gray-200 text-gray-800';
        const priorityClass = priorityStyles[alert.prioridade] || '';
        
        return (
          <div
            key={alert.id}
            onClick={() => handleClick(alert)}
            className={`relative p-4 rounded-lg border ${colorClass} ${priorityClass} cursor-pointer 
              transition-all duration-200 hover:shadow-md hover:scale-[1.01] group`}
          >
            <button
              onClick={(e) => handleDismiss(alert.id, e)}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-black/10 
                opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Fechar alerta"
            >
              <X className="w-4 h-4" />
            </button>
            
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-white/50">
                <Icon className="w-5 h-5" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-base leading-tight mb-1">
                  {alert.titulo}
                </h4>
                <p className="text-sm opacity-80 line-clamp-2">
                  {alert.mensagem}
                </p>
                
                {alert.cta_label && (
                  <div className="mt-2 flex items-center gap-1 text-sm font-medium">
                    <span>{alert.cta_label}</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Componente para barra de alertas no topo do site
export function PublicAlertBar({ municipioId }) {
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBarAlert();
    // Check if bar was dismissed in this session
    const dismissed = sessionStorage.getItem('dismissedAlertBar');
    if (dismissed) setVisible(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioId]);

  const loadBarAlert = async () => {
    try {
      const params = { exibir_barra: 'true' };
      if (municipioId) params.municipio_id = municipioId;
      
      const alerts = await alertsApi.listPublic(params);
      // Get the highest priority alert
      if (alerts.length > 0) {
        const priorityOrder = { alta: 0, media: 1, baixa: 2 };
        alerts.sort((a, b) => (priorityOrder[a.prioridade] || 2) - (priorityOrder[b.prioridade] || 2));
        setAlert(alerts[0]);
      }
    } catch (error) {
      console.error('Erro ao carregar alerta da barra:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    sessionStorage.setItem('dismissedAlertBar', 'true');
  };

  const handleClick = async () => {
    if (!alert) return;
    
    // Track click
    try {
      await alertsApi.trackPublic(alert.id, 'click');
    } catch (error) {
      console.error('Erro ao rastrear clique:', error);
    }
    
    if (alert.cta_url) {
      if (alert.cta_url.startsWith('http')) {
        window.open(alert.cta_url, '_blank');
      } else {
        navigate(alert.cta_url);
      }
    }
  };

  if (loading || !alert || !visible) return null;

  const bgColors = {
    alta: 'bg-gradient-to-r from-red-600 to-red-500',
    media: 'bg-gradient-to-r from-[#56007F] to-[#7B1FA2]',
    baixa: 'bg-gradient-to-r from-green-600 to-green-500',
  };

  const bgColor = bgColors[alert.prioridade] || bgColors.media;

  return (
    <div className={`${bgColor} text-white py-2 px-4 relative z-50`}>
      <div className="container mx-auto flex items-center justify-center gap-4">
        <div 
          className="flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity"
          onClick={handleClick}
        >
          <Bell className="w-4 h-4 animate-pulse" />
          <span className="text-sm font-medium">{alert.titulo}</span>
          {alert.cta_label && (
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full flex items-center gap-1">
              {alert.cta_label}
              <ChevronRight className="w-3 h-3" />
            </span>
          )}
        </div>
        
        <button
          onClick={handleDismiss}
          className="absolute right-4 p-1 rounded-full hover:bg-white/20 transition-colors"
          aria-label="Fechar barra"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default PublicAlertsHome;
